import {
  Euler,
  Matrix4,
  ProjectionType,
  Quaternion,
  Vector3,
} from '@technology/web3d';
import { Camera } from '@data-access/connect/tc-tcps';
import {
  convertMillimetersToMeters,
  convertMetersToMillimeters,
} from '@trimble/utilities';

// Constants
const PITCH_PI_EPSILON = 0.001;
const PITCH_NEAR_PI_THRESHOLD = 0.9999;
const DEFAULT_CAMERA_DISTANCE_MM = 1000;

/**
 * Trimble rotation representation with pitch and yaw
 */
export interface TrimbleRotation {
  readonly pitch: number;
  readonly yaw: number;
}

/**
 * Comprehensive viewer camera data interface
 */
export interface ViewerCameraData {
  readonly position?: Vector3;
  readonly quaternion?: Quaternion;
  readonly rotation?: TrimbleRotation;
  readonly direction?: Vector3;
  readonly projectionType?: ProjectionType;
  readonly fieldOfView?: number;
  readonly orthoSize?: number;
}

/**
 * Core camera position and direction calculation
 */
export function calculateCameraPositionAndDirection(camera: Camera): {
  position: Vector3;
  direction: Vector3;
} {
  const direction = new Vector3(
    Math.sin(camera.pitch) * Math.sin(camera.yaw),
    -Math.sin(camera.pitch) * Math.cos(camera.yaw),
    Math.cos(camera.pitch)
  );

  const position = new Vector3(
    convertMillimetersToMeters(camera.targetX + camera.distance * direction.x),
    convertMillimetersToMeters(camera.targetY + camera.distance * direction.y),
    convertMillimetersToMeters(camera.targetZ + camera.distance * direction.z)
  );

  return { position, direction };
}

/**
 * Convert pitch and yaw angles to a quaternion
 */
export function pitchYawToQuaternion(pitch: number, yaw: number): Quaternion {
  let adjustedYaw = yaw;
  if (Math.abs(pitch - Math.PI) < PITCH_PI_EPSILON) {
    adjustedYaw += Math.PI;
  }
  const euler = new Euler().set(pitch, 0, adjustedYaw, 'YZX');
  return new Quaternion().setFromEuler(euler);
}

/**
 * Convert quaternion to Trimble rotation (pitch/yaw)
 */
export function quaternionToRotation(q: Quaternion): TrimbleRotation {
  const forward = new Vector3(0, 0, -1).applyQuaternion(q);
  const pitch =
    Math.PI -
    Math.atan2(
      Math.sqrt(forward.x * forward.x + forward.y * forward.y),
      forward.z
    );

  if (Math.abs(forward.z) > PITCH_NEAR_PI_THRESHOLD) {
    forward.set(0, 1, 0).applyQuaternion(q);
  }
  const yaw = -Math.atan2(forward.x, forward.y);
  return { pitch, yaw };
}

/**
 * Convert Connect camera to viewer camera with optional legacy quaternion correction
 * @param camera - The Connect camera data to convert
 * @param applyLegacyCorrection - Whether to apply legacy quaternion correction (default: true for TCWeb compatibility)
 */
export function convertConnectCameraToViewerCamera(
  camera: Camera,
  applyLegacyCorrection: boolean = true
): ViewerCameraData {
  const { position } = calculateCameraPositionAndDirection(camera);
  const rotation: TrimbleRotation = { pitch: camera.pitch, yaw: camera.yaw };

  const rawQuaternion = pitchYawToQuaternion(camera.pitch, camera.yaw);
  const quaternion = applyLegacyCorrection
    ? rawQuaternion.multiply(
        new Quaternion().setFromEuler(new Euler(0, Math.PI, 0))
      )
    : rawQuaternion;

  return {
    position,
    quaternion,
    rotation,
    direction: undefined, // Will be calculated if needed
    projectionType: camera.projectionType as ProjectionType,
    fieldOfView: camera.viewAngle,
    orthoSize: camera.viewScale,
  };
}

/**
 * Camera conversion from Connect camera details to Viewer camera data
 */
export function convertConnectCameraDetailsToViewerCamera(
  position: { x: number; y: number; z: number },
  quaternion: Quaternion | undefined,
  rotation: TrimbleRotation | undefined,
  projectionType: 'ortho' | 'perspective' | undefined,
  viewAngle: number | undefined,
  viewScale: number | undefined
): ViewerCameraData {
  return {
    position: new Vector3(
      convertMillimetersToMeters(position.x),
      convertMillimetersToMeters(position.y),
      convertMillimetersToMeters(position.z)
    ),
    // Legacy camera quaternion was turned backwards
    quaternion: quaternion
      ? quaternion
          .clone()
          .multiply(new Quaternion().setFromEuler(new Euler(0, Math.PI, 0)))
      : undefined,
    rotation,
    direction: undefined,
    projectionType: projectionType as ProjectionType,
    fieldOfView: viewAngle,
    orthoSize: viewScale,
  };
}

/**
 * Convert viewer point from Connect coordinate system (mm) to viewer coordinate system (m)
 */
export function convertConnectPointToViewerPoint(
  x: number,
  y: number,
  z: number
): Vector3 {
  return new Vector3(
    convertMillimetersToMeters(x),
    convertMillimetersToMeters(y),
    convertMillimetersToMeters(z)
  );
}

/**
 * Convert viewer direction from Connect coordinate system
 */
export function convertConnectDirectionToViewerDirection(
  x: number,
  y: number,
  z: number
): Vector3 {
  return new Vector3(-x, -y, -z);
}

/**
 * Get camera up direction from viewer camera data
 */
export function getCameraUpDirection(
  camera: ViewerCameraData
): Vector3 | undefined {
  if (camera.quaternion) {
    const matrix = new Matrix4();
    matrix.makeRotationFromQuaternion(camera.quaternion);
    const x = new Vector3();
    const y = new Vector3();
    const z = new Vector3();
    matrix.extractBasis(x, y, z);
    return y;
  }
  return undefined;
}

/**
 * Convert viewer camera data to Connect camera format
 * This is the inverse operation of convertConnectCameraToViewerCamera
 */
export function convertViewerCameraToConnectCamera(
  viewerCamera: ViewerCameraData,
  cameraId: string = 'default-camera'
): Camera {
  const position = viewerCamera.position ?? new Vector3(0, 0, 0);
  const quaternion = viewerCamera.quaternion ?? new Quaternion();

  const rotation = viewerCamera.rotation ?? quaternionToRotation(quaternion);

  const direction = new Vector3(
    Math.sin(rotation.pitch) * Math.sin(rotation.yaw),
    -Math.sin(rotation.pitch) * Math.cos(rotation.yaw),
    Math.cos(rotation.pitch)
  );

  const distance = DEFAULT_CAMERA_DISTANCE_MM;
  const targetX =
    convertMetersToMillimeters(position.x) - distance * direction.x;
  const targetY =
    convertMetersToMillimeters(position.y) - distance * direction.y;
  const targetZ =
    convertMetersToMillimeters(position.z) - distance * direction.z;

  const upVector = getCameraUpDirection(viewerCamera);

  return {
    cameraId,
    targetX,
    targetY,
    targetZ,
    distance,
    pitch: rotation.pitch,
    yaw: rotation.yaw,
    projectionType:
      // eslint-disable-next-line sonarjs/different-types-comparison -- ProjectionType is a string const enum whose Ortho member is 'ortho'
      viewerCamera.projectionType === 'ortho' ? 'ortho' : 'perspective',
    viewAngle: viewerCamera.fieldOfView,
    viewScale: viewerCamera.orthoSize,
    upX: upVector?.x,
    upY: upVector?.y,
    upZ: upVector?.z,
  };
}
